// PDF generator for finished legal articles.
// Enforces the Task 1 brief: Times New Roman 12pt, 1.5 spacing, justified,
// with heading hierarchy, numbered footnotes and a bibliography section.
import { jsPDF } from "jspdf";

export type ArticleSection = {
  heading: string;
  body: string;
};

export type ArticlePdfInput = {
  title: string;
  author: string;
  affiliation?: string;
  citationStyleLabel: string;
  abstract?: string;
  sections: ArticleSection[];
  footnotes: string[]; // ordered by [n]
  bibliography: string[]; // one entry per line
};

const PAGE_WIDTH = 210; // A4
const PAGE_HEIGHT = 297;
const MARGIN = 25.4; // 1 inch
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const LINE_HEIGHT = 6.35; // 12pt * 1.5 in mm

export function buildArticlePdf(input: ArticlePdfInput): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFont("times", "normal");
  doc.setFontSize(12);

  let y = MARGIN;
  const ensure = (needed: number) => {
    if (y + needed > PAGE_HEIGHT - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  // Title
  doc.setFont("times", "bold");
  doc.setFontSize(16);
  const titleLines = doc.splitTextToSize(input.title || "Untitled Article", CONTENT_WIDTH);
  ensure(titleLines.length * 7);
  doc.text(titleLines, PAGE_WIDTH / 2, y, { align: "center" });
  y += titleLines.length * 7 + 2;

  // Author line
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  const authorLine = [input.author, input.affiliation].filter(Boolean).join(" · ");
  if (authorLine) {
    ensure(6);
    doc.text(authorLine, PAGE_WIDTH / 2, y, { align: "center" });
    y += 6;
  }

  // Citation style badge
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  ensure(5);
  doc.text(
    `Citation style: ${input.citationStyleLabel}`,
    PAGE_WIDTH / 2,
    y,
    { align: "center" },
  );
  y += 8;

  // Abstract
  if (input.abstract && input.abstract.trim()) {
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    ensure(LINE_HEIGHT);
    doc.text("Abstract", MARGIN, y);
    y += LINE_HEIGHT;
    doc.setFont("times", "normal");
    y = writeJustified(doc, input.abstract.trim(), y, ensure);
    y += LINE_HEIGHT / 2;
  }

  // Sections
  input.sections.forEach((section, idx) => {
    if (!section.heading && !section.body) return;
    doc.setFont("times", "bold");
    doc.setFontSize(13);
    const heading = section.heading?.trim()
      ? `${idx + 1}. ${section.heading.trim()}`
      : `${idx + 1}.`;
    const hLines = doc.splitTextToSize(heading, CONTENT_WIDTH);
    ensure(hLines.length * LINE_HEIGHT + 2);
    y += 2;
    doc.text(hLines, MARGIN, y);
    y += hLines.length * LINE_HEIGHT;

    doc.setFont("times", "normal");
    doc.setFontSize(12);
    if (section.body?.trim()) {
      y = writeJustified(doc, section.body.trim(), y, ensure);
    }
    y += LINE_HEIGHT / 2;
  });

  // Footnotes
  const notes = input.footnotes.filter((n) => n && n.trim());
  if (notes.length) {
    doc.addPage();
    y = MARGIN;
    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.text("Footnotes", MARGIN, y);
    y += LINE_HEIGHT + 1;
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    notes.forEach((note, i) => {
      const text = `${i + 1}. ${note.trim()}`;
      const lines = doc.splitTextToSize(text, CONTENT_WIDTH);
      ensure(lines.length * (LINE_HEIGHT - 1));
      doc.text(lines, MARGIN, y);
      y += lines.length * (LINE_HEIGHT - 1) + 1;
    });
  }

  // Bibliography
  const bib = input.bibliography.filter((b) => b && b.trim());
  if (bib.length) {
    doc.addPage();
    y = MARGIN;
    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.text("Bibliography", MARGIN, y);
    y += LINE_HEIGHT + 1;
    doc.setFont("times", "normal");
    doc.setFontSize(12);
    bib.forEach((entry) => {
      const lines = doc.splitTextToSize(entry.trim(), CONTENT_WIDTH - 6);
      ensure(lines.length * LINE_HEIGHT + 1);
      // Hanging indent: first line at margin, rest indented 6mm
      lines.forEach((ln: string, idx: number) => {
        doc.text(ln, MARGIN + (idx === 0 ? 0 : 6), y);
        y += LINE_HEIGHT - 0.5;
      });
      y += 1;
    });
  }

  // Page numbers footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("times", "normal");
    doc.setFontSize(9);
    doc.text(
      `${i} / ${pageCount}`,
      PAGE_WIDTH / 2,
      PAGE_HEIGHT - 10,
      { align: "center" },
    );
  }

  return doc;
}

function writeJustified(
  doc: jsPDF,
  text: string,
  y: number,
  ensure: (n: number) => void,
): number {
  const paragraphs = text.split(/\n\s*\n/);
  for (let p = 0; p < paragraphs.length; p++) {
    const para = paragraphs[p].replace(/\s+\n/g, " ").replace(/\n/g, " ").trim();
    if (!para) continue;
    const lines: string[] = doc.splitTextToSize(para, CONTENT_WIDTH);
    lines.forEach((line, idx) => {
      ensure(LINE_HEIGHT);
      const isLast = idx === lines.length - 1;
      if (isLast || line.trim().split(/\s+/).length < 2) {
        doc.text(line, MARGIN, y);
      } else {
        // simulate justification with wordSpacing via drawing at computed x offsets
        const words = line.trim().split(/\s+/);
        const strWidth = doc.getTextWidth(words.join(" "));
        const gapCount = words.length - 1;
        const extra = gapCount > 0 ? (CONTENT_WIDTH - strWidth) / gapCount : 0;
        let cursorX = MARGIN;
        words.forEach((w, wi) => {
          doc.text(w, cursorX, y);
          cursorX += doc.getTextWidth(w) + doc.getTextWidth(" ") + extra;
          void wi;
        });
      }
      y += LINE_HEIGHT;
    });
    y += 1;
  }
  return y;
}
